// Destino dos itens criados pela impressão, campo destino_itens da
// configuração de impressão:
//   ausente ou ""   -> padrão (iNOVAONE, comportamento de sempre)
//   "inovaone"      -> iNOVAONE, explícito (mesmo efeito do padrão)
//   "inovaid"       -> iNOVAID, pelo token de integração
//   "personalizado" -> POST para a URL informada pelo cliente
// Os tokens não chegam ao navegador (a API devolve mascarado); o envio para
// iNOVAID e URL personalizada passa pela API (services/destino_itens_api.ts).
// Funções puras, sem dependência de contexto, para o ItemContext e as telas.

export type DestinoItens = "inovaone" | "inovaid" | "personalizado";

/** Valor gravado em destino_itens; "" (ou ausente) é o padrão. */
export type ValorDestinoItens = "" | DestinoItens;

/** Opção do seletor da tela; "padrao" vira "" ao salvar. */
export type OpcaoDestinoItens = "padrao" | DestinoItens;

export const OPCOES_DESTINO_ITENS: {
  valor: OpcaoDestinoItens;
  rotulo: string;
  descricao: string;
}[] = [
  {
    valor: "padrao",
    rotulo: "Padrão",
    descricao: "Os itens são criados no iNOVAONE, como nas demais contas.",
  },
  {
    valor: "inovaone",
    rotulo: "iNOVAONE",
    descricao: "Os itens são criados no iNOVAONE.",
  },
  {
    valor: "inovaid",
    rotulo: "iNOVAID",
    descricao:
      "Os itens são criados na conta do iNOVAID dona do token de integração.",
  },
  {
    valor: "personalizado",
    rotulo: "URL personalizada",
    descricao:
      "Os itens impressos são enviados por POST, em JSON, para a URL informada.",
  },
];

export function destinoDosItens(
  config?: { destino_itens?: string } | null,
): DestinoItens {
  const valor = config?.destino_itens;
  return valor === "inovaid" || valor === "personalizado" ? valor : "inovaone";
}

/** Opção do seletor para a configuração (ausente ou "" = "padrao"). */
export function opcaoDoDestino(
  config?: { destino_itens?: string } | null,
): OpcaoDestinoItens {
  const valor = config?.destino_itens;
  return valor === "inovaone" || valor === "inovaid" || valor === "personalizado"
    ? valor
    : "padrao";
}

/** Valor a gravar em destino_itens para a opção escolhida. */
export function valorDoDestino(opcao: OpcaoDestinoItens): ValorDestinoItens {
  return opcao === "padrao" ? "" : opcao;
}

export function ehDestinoInovaid(config?: { destino_itens?: string } | null) {
  return destinoDosItens(config) === "inovaid";
}

/** iNOVAID ou URL personalizada: os itens não vão para o iNOVAONE. */
export function ehDestinoExterno(config?: { destino_itens?: string } | null) {
  return destinoDosItens(config) !== "inovaone";
}

/** A API devolve os tokens salvos como "********" + 4 últimos caracteres. */
export function tokenMascarado(valor?: string | null) {
  return typeof valor === "string" && valor.startsWith("********");
}

const NOMES_INTERNOS = [
  /^localhost$/i,
  /\.localhost$/i,
  /\.local$/i,
  /\.internal$/i,
  /\.lan$/i,
  /\.home\.arpa$/i,
];

function ipv4Interno(ip: string) {
  const [a, b] = ip.split(".").map(Number);
  return (
    a === 0 ||
    a === 10 ||
    a === 127 ||
    (a === 100 && b >= 64 && b <= 127) ||
    (a === 169 && b === 254) ||
    (a === 172 && b >= 16 && b <= 31) ||
    (a === 192 && b === 168) ||
    (a === 198 && (b === 18 || b === 19)) ||
    a >= 224
  );
}

/**
 * Conferência da URL personalizada na tela (a API confere de novo, inclusive
 * o IP resolvido). Devolve a mensagem de erro ou null.
 */
export function validarUrlDestino(valor?: string | null): string | null {
  const texto = (valor ?? "").trim();
  if (!texto) return "Informe a URL de destino dos itens impressos.";
  let url: URL;
  try {
    url = new URL(texto);
  } catch {
    return "A URL de destino é inválida.";
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") {
    return "A URL de destino deve começar com http:// ou https://.";
  }
  if (url.username || url.password) {
    return "A URL de destino não pode conter usuário e senha. Informe o token no campo próprio.";
  }
  const host = url.hostname.replace(/^\[|\]$/g, "");
  const interno =
    /^\d{1,3}(\.\d{1,3}){3}$/.test(host)
      ? ipv4Interno(host)
      : host.includes(":")
        ? /^(::1?|f[cd]|fe[89ab]|::ffff:)/i.test(host)
        : !host.includes(".") || NOMES_INTERNOS.some((r) => r.test(host));
  if (interno) {
    return "A URL de destino não pode apontar para rede interna ou para o próprio servidor.";
  }
  return null;
}

/** Nome de cabeçalho HTTP válido (vazio = Authorization: Bearer). */
export function validarCabecalhoToken(valor?: string | null): string | null {
  const texto = (valor ?? "").trim();
  if (!texto) return null;
  return /^[A-Za-z0-9!#$%&'*+.^_`|~-]{1,100}$/.test(texto)
    ? null
    : "Nome do cabeçalho do token inválido. Use apenas letras, números e hífen.";
}

/**
 * Conferência do destino antes de salvar a configuração.
 * @returns mensagem para a tela, ou null se pode salvar
 */
export function validarDestinoAntesDeSalvar(config: {
  destino_itens?: string;
  inovaid_token_integracao?: string;
  destino_url_personalizada?: string;
  destino_cabecalho_token_personalizado?: string;
}): string | null {
  const opcao = opcaoDoDestino(config);
  if (opcao === "inovaid" && !config.inovaid_token_integracao) {
    return "Informe o token de integração do iNOVAID.";
  }
  if (opcao === "personalizado") {
    return (
      validarUrlDestino(config.destino_url_personalizada) ??
      validarCabecalhoToken(config.destino_cabecalho_token_personalizado)
    );
  }
  return null;
}

// Item no formato da rota POST /integracao/itens do iNOVAID (o mesmo vai
// para a URL personalizada)
export type ItemInovaid = {
  tag: string;
  nome: string;
  categoria: string;
  ean?: string;
  id_externo?: string;
  inf_compl_1?: string;
  inf_compl_2?: string;
  inf_compl_3?: string;
  inf_compl_4?: string;
  inf_compl_5?: string;
  inf_compl_6?: string;
  inf_compl_7?: string;
  inf_compl_8?: string;
  inf_compl_9?: string;
  inf_compl_10?: string;
  inf_compl_11?: string;
  inf_compl_12?: string;
};

// Campos mínimos do item da impressão usados no envio ao iNOVAID
export type ItemImpressaoParaInovaid = {
  tag: string;
  codigo?: string;
  descricao?: string;
  id_externo?: string;
  inf_compl_1?: string;
  inf_compl_2?: string;
  inf_compl_3?: string;
  inf_compl_4?: string;
  inf_compl_5?: string;
  inf_compl_6?: string;
  inf_compl_7?: string;
  inf_compl_8?: string;
  inf_compl_9?: string;
  inf_compl_10?: string;
  inf_compl_11?: string;
  inf_compl_12?: string;
};

export const MAX_ITENS_POR_LOTE_INOVAID = 500;

const CAMPOS_INF_COMPL = [
  "inf_compl_1",
  "inf_compl_2",
  "inf_compl_3",
  "inf_compl_4",
  "inf_compl_5",
  "inf_compl_6",
  "inf_compl_7",
  "inf_compl_8",
  "inf_compl_9",
  "inf_compl_10",
  "inf_compl_11",
  "inf_compl_12",
] as const;

const texto = (valor: unknown) =>
  valor === undefined || valor === null ? "" : String(valor).trim();

/**
 * Converte o item da impressão no item do iNOVAID.
 * Nome e categoria = descrição do produto (no iNOVAONE a categoria é o produto);
 * sem descrição, usa o código. O código vai como EAN; ID externo e informações
 * complementares 1 a 12 seguem iguais.
 */
export function montarItemInovaid(item: ItemImpressaoParaInovaid): ItemInovaid {
  const nome = texto(item.descricao) || texto(item.codigo);
  const convertido: ItemInovaid = {
    tag: texto(item.tag).toUpperCase(),
    nome,
    categoria: nome,
  };
  const ean = texto(item.codigo);
  if (ean) convertido.ean = ean;
  const id_externo = texto(item.id_externo);
  if (id_externo) convertido.id_externo = id_externo;
  for (const campo of CAMPOS_INF_COMPL) {
    const valor = texto(item[campo]);
    if (valor) convertido[campo] = valor;
  }
  return convertido;
}

export function dividirEmLotes<T>(lista: T[], tamanho: number): T[][] {
  const lotes: T[][] = [];
  for (let i = 0; i < lista.length; i += tamanho) {
    lotes.push(lista.slice(i, i + tamanho));
  }
  return lotes;
}

// Devolve, das tags informadas, as que constam na lista de existentes (sem diferenciar maiúsculas)
export function tagsQueExistem(tags: string[], existentes: string[]) {
  const conjunto = new Set(existentes.map((tag) => tag.toUpperCase()));
  return tags.filter((tag) => conjunto.has(String(tag).toUpperCase()));
}
